// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import type { Root } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { EventType, InteractionRequiredAuthError, ServerError } from '@azure/msal-browser';
import type { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import App from './App';
import ProvisioningApp from './ProvisioningApp';
import SignInErrorPage from './components/auth/SignInErrorPage';
import { loginRequest } from './config/authConfig';
import { useSessionGuardStore } from './stores/sessionGuardStore';

// Which application this build is. `provisioning` is the Qcells-internal
// org/site provisioning tool, deployed to its own Static Web App with its own
// app registration; anything else builds the customer app. CRA inlines
// REACT_APP_* at build time, so the untaken branch drops out of the bundle.
const Surface = process.env.REACT_APP_SURFACE === 'provisioning' ? ProvisioningApp : App;

/**
 * True when this script is executing inside the popup window opened by
 * `loginPopup` (LockOverlay's "Unlock" / "sign in as a different user").
 * That popup loads this same SPA bundle at the same origin, so its own
 * `bootstrap()` runs too — but the auth response it briefly sees in its URL
 * belongs to the *opener's* MSAL instance/request cache, not this fresh one.
 * The opener's `PopupClient` reads the popup's `location.href` directly and
 * closes it; this window's own `handleRedirectPromise()` must not also try
 * to consume that response, or it throws `no_token_request_cache_error`
 * (no matching request in this window's — empty — temporary cache).
 */
const isPopupWindow = () => window.opener !== null && window.opener !== window;

/**
 * True when Entra itself refused the sign-in (it redirected back with an
 * error, e.g. AADSTS500571 account disabled). Anything else that
 * `handleRedirectPromise()` throws is the browser failing to process a
 * response, e.g. a stale `#code`/state reached with the back button
 * (`no_token_request_cache_error`, state mismatch).
 */
const isSignInRefused = (error: unknown) =>
  error instanceof ServerError || error instanceof InteractionRequiredAuthError;

/**
 * Renders the sign-in error page in place of the app. E.g. Entra refused the
 * sign-in (AADSTS500571, account disabled); left uncaught, nothing renders
 * and the user sees a blank page.
 */
function renderSignInError(
  msalInstance: PublicClientApplication,
  root: Root,
  error: unknown,
  initialized: boolean
) {
  console.error('[Helios] Sign-in failed during startup', error);
  const toSignInPage = () => window.location.assign('/login');
  const signInAgain = initialized
    ? () =>
        msalInstance
          .loginRedirect({ ...loginRequest, prompt: 'select_account' })
          .catch(redirectError => {
            console.error('[Helios] Sign in again failed', redirectError);
            toSignInPage();
          })
    : toSignInPage;
  root.render(
    <React.StrictMode>
      <SignInErrorPage error={error} onSignInAgain={signInAgain} />
    </React.StrictMode>
  );
}

/**
 * Bootstrap MSAL before rendering.
 *
 * 1. `handleRedirectPromise()` processes any CIAM redirect response
 *    (auth code in the URL) so the app has tokens before React renders.
 * 2. Set the active account so MSAL knows which identity to use for
 *    silent token acquisition.
 *
 * If either step fails, renders a sign-in error page instead of the app,
 * unless the failure is only an unusable redirect response and a signed-in
 * account is already cached: then the app starts with that account.
 */
export async function bootstrap(msalInstance: PublicClientApplication, root: Root): Promise<void> {
  let initialized = false;
  let hadActiveAccountBeforeBootstrap = false;
  let response: AuthenticationResult | null;
  try {
    // Process CIAM redirect response (no-op if this isn't a redirect return).
    // No MSAL public API (including `getAllAccounts()`) may be called before
    // `initialize()` resolves — it throws `uninitialized_public_client_application`.
    await msalInstance.initialize();
    initialized = true;

    // Captured right after initialize(), before any account is resolved below,
    // so the LOGIN_SUCCESS callback can tell "establishing a session" from
    // "cross-tab/-popup sync of an already-active one" — see the comment on
    // that callback.
    hadActiveAccountBeforeBootstrap = msalInstance.getAllAccounts().length > 0;

    response = isPopupWindow() ? null : await msalInstance.handleRedirectPromise();
  } catch (error) {
    if (!initialized || isSignInRefused(error) || msalInstance.getAllAccounts().length === 0) {
      renderSignInError(msalInstance, root, error, initialized);
      return;
    }
    // Already signed in; only this redirect response is unusable.
    console.warn('[Helios] Ignored an unusable sign-in response; using the cached account', error);
    response = null;
  }

  if (response?.account) {
    msalInstance.setActiveAccount(response.account);
    // A real interactive sign-in via loginRedirect just completed: start a
    // fresh 12h clock (ADO-25800).
    useSessionGuardStore.getState().setSignInAt(Date.now());
  } else if (!msalInstance.getActiveAccount()) {
    // Pick the first account if one exists in cache (e.g. page refresh)
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  // Keep active account in sync when login succeeds in other tabs/popups.
  // Only stamp `signInAt` here when this tab had no active account before
  // bootstrap ran (a genuinely new session reaching us via a popup, e.g.
  // `loginPopup`, which never goes through `handleRedirectPromise` above) —
  // otherwise every cross-tab sync of an already-active session would keep
  // pushing the 12h absolute limit back, defeating it.
  msalInstance.addEventCallback(event => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const result = event.payload as AuthenticationResult;
      if (result.account) {
        msalInstance.setActiveAccount(result.account);
        if (!hadActiveAccountBeforeBootstrap) {
          useSessionGuardStore.getState().setSignInAt(Date.now());
        }
      }
    }
  });

  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <Surface />
      </MsalProvider>
    </React.StrictMode>
  );
}
